import React, { useEffect, useState } from 'react';
import { PlaidLink } from 'react-plaid-link';
import getEnvConfig from '../../../../config';
import Sidebar from '../../../../Components/Sidebar';
import { fetchToken } from '../../../../Auth';
import { Container, Row, Col, Card, Button, Form, Modal, Tooltip, OverlayTrigger, Alert } from 'react-bootstrap';
import axios from 'axios';

const ACHTransferPage = () => {
  const { backendUrl } = getEnvConfig();
  const [balance, setBalance] = useState(0);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [linkToken, setLinkToken] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [accountId, setAccountId] = useState(null);
  const [publicToken, setPublicToken] = useState(null);
  const [transferAmount, setTransferAmount] = useState(0);
  const token = fetchToken();
  const [showChequeModal, setShowChequeModal] = useState(false);
  const [chequeAmount, setChequeAmount] = useState('');
  const [payableTo, setPayableTo] = useState('');
  const [shippingAddress, setShippingAddress] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [chequeError, setChequeError] = useState('');
  const [chequeSuccess, setChequeSuccess] = useState('');
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [submittingCheque, setSubmittingCheque] = useState(false);

  useEffect(() => {
    const fetchBalanceAndLinkToken = async () => {
      try {
        const balanceResponse = await fetch(`${backendUrl}/payments/balance`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_token: token })
        });
        const balanceData = await balanceResponse.json();
        setBalance(balanceData.balance);
        // Set transferAmount to max(0, balance - 5)
        setTransferAmount(Math.max(0, (balanceData.balance ?? 0) - 5));
        // Fetch payment history from backend
        const historyResponse = await fetch(`${backendUrl}/payments/history`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_token: token })
        });
        const historyData = await historyResponse.json();
        setPaymentHistory(historyData.payments ?? []);

        const linkResponse = await fetch(`${backendUrl}/payments/plaid_link_token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_token: token })
        });
        const linkData = await linkResponse.json();
        setLinkToken(linkData.link_token);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchBalanceAndLinkToken();
  }, [backendUrl, token]);

  const onSuccess = async (publicToken, metadata) => {
    setPublicToken(publicToken);
    try {
      await fetch(`${backendUrl}/payments/connect_account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          public_token: publicToken,
          user_token: token
        })
      });
      setAccountId(metadata.accounts[0].id);
      alert("Account successfully connected!");
    } catch (error) {
      console.error('Error connecting account:', error);
    }
  };

  const initiateTransfer = async () => {
    try {
      setIsLoading(true);
      await fetch(`${backendUrl}/payments/create_transfer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_token: token,
          public_token: publicToken,
          amount: transferAmount,
          account_id: accountId
        })
      });
      
      // Update the balance and payment history immediately after initiating the transfer
      setBalance((prevBalance) => prevBalance - transferAmount);

      // Add the new transfer with "Pending" status to the payment history
      const newPayment = {
        date: new Date().toISOString().split('T')[0],  // format as YYYY-MM-DD
        amount: transferAmount,
        status: "Pending"
      };
      setPaymentHistory((prevHistory) => [newPayment, ...prevHistory]);

      alert("Transfer initiated successfully!");
    } catch (error) {
      console.error('Error initiating transfer:', error);
      alert("Transfer failed.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAmountChange = (event) => {
    const value = parseFloat(event.target.value);
    if (value <= balance) {
      setTransferAmount(value);
    } else {
      alert("Transfer amount cannot exceed available balance.");
    }
  };

  const chequeFee = 5;
  const maxChequeAmount = Math.max(0, balance - chequeFee);

  // Tooltip for Plaid button
  const plaidTooltip = (
    <Tooltip id="plaid-tooltip">Coming shortly</Tooltip>
  );

  // Handler for sending OTP
  const handleSendOtp = async () => {
    setSendingOtp(true);
    setOtpError('');
    try {
      const resp = await axios.post(`${backendUrl}/users/password-reset/send-otp/`, { user_token: token });
      if (resp.data.verification_sid) setOtpSent(true);
      else setOtpError('Failed to send OTP.');
    } catch (e) {
      setOtpError('Failed to send OTP.');
    } finally {
      setSendingOtp(false);
    }
  };

  // Handler for verifying OTP
  const handleVerifyOtp = async () => {
    setVerifyingOtp(true);
    setOtpError('');
    try {
      const resp = await axios.post(`${backendUrl}/users/password-reset/verify-otp/`, { user_token: token, otp });
      if (resp.data.success) setOtpVerified(true);
      else setOtpError('Invalid OTP.');
    } catch (e) {
      setOtpError('Invalid OTP.');
    } finally {
      setVerifyingOtp(false);
    }
  };

  // Handler for submitting cheque request
  const handleSubmitCheque = async () => {
    setSubmittingCheque(true);
    setChequeError('');
    setChequeSuccess('');
    if (!chequeAmount || isNaN(chequeAmount) || chequeAmount < 5 || chequeAmount > maxChequeAmount) {
      setChequeError(`Requested amount must be between $5 and $${maxChequeAmount}`);
      setSubmittingCheque(false);
      return;
    }
    if (!payableTo || !shippingAddress) {
      setChequeError('Please fill all fields.');
      setSubmittingCheque(false);
      return;
    }
    try {
      await axios.post(`${backendUrl}/utils/gmail_stub`, {
        user_token: token,
        to: 'rishabh.mehandru@evchron.com',
        subject: 'Cheque Payment Request',
        body: `Requested Amount: $${chequeAmount}\nCheck Payable To: ${payableTo}\nShipping Address: ${shippingAddress}`
      });
      setChequeSuccess('Cheque request submitted successfully!');
      setShowChequeModal(false);
    } catch (e) {
      setChequeError('Failed to submit cheque request.');
    } finally {
      setSubmittingCheque(false);
    }
  };

  return (
    <div style={{ display: 'flex' }}>
      <Sidebar />
      <Container fluid style={{ marginLeft: '5px' }}>
        <Row className="justify-content-center my-5">
          <Col md={8}>
            <Card className="shadow-sm p-4 text-center">
              <Card.Header>
                <h3>Balance Available for Withdrawal</h3>
                <p>${(balance ?? 0).toFixed(2)}</p>
              </Card.Header>
              <Card.Body>
                <Form.Group as={Row} className="mb-3" controlId="transferAmount">
                  <Form.Label column sm={4}>
                    Transfer Amount
                  </Form.Label>
                  <Col sm={8}>
                    <Form.Control
                      type="number"
                      value={transferAmount}
                      onChange={handleAmountChange}
                      min="0"
                      max={Math.max(0, balance - 5)}
                      step="0.01"
                      placeholder="Enter transfer amount"
                    />
                  </Col>
                </Form.Group>
                <div className="d-flex justify-content-between align-items-center">
                  <OverlayTrigger placement="top" overlay={plaidTooltip}>
                    <span className="d-inline-block">
                      <Button variant="secondary" disabled style={{ pointerEvents: 'none', opacity: 0.5 }}>
                        Withdraw via Plaid (ACH)
                      </Button>
                    </span>
                  </OverlayTrigger>
                  <Button
                    variant="success"
                    onClick={initiateTransfer}
                    disabled={!accountId || isLoading}
                  >
                    {isLoading ? "Processing..." : "Initiate Transfer"}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Row className="justify-content-center my-5">
          <Col md={8}>
            <Card className="shadow-sm p-4">
              <Card.Header>
                <h4>Payment History</h4>
              </Card.Header>
              <Card.Body>
                <ul>
                  {paymentHistory.map((payment, index) => (
                    <li key={index}>
                      {payment.date} - ${(payment.amount ?? 0).toFixed(2)} - {payment.status}
                    </li>
                  ))}
                </ul>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Row className="justify-content-center my-5">
          <Col md={8}>
            <Button variant="primary" className="ml-2" onClick={() => setShowChequeModal(true)}>
              Request Cheque Payment
            </Button>
          </Col>
        </Row>
        <Modal show={showChequeModal} onHide={() => setShowChequeModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Request Cheque Payment</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group>
                <Form.Label>Requested Amount ($)</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  max={maxChequeAmount}
                  value={chequeAmount}
                  onChange={e => setChequeAmount(e.target.value)}
                  disabled={maxChequeAmount <1}
                />
                <Form.Text className="text-muted">
                  Available balance: ${(balance ?? 0).toFixed(2)}<br />
                  A $5 cheque processing & shipping fee will be deducted from your balance.
                </Form.Text>
              </Form.Group>
              <Form.Group>
                <Form.Label>Check Payable To</Form.Label>
                <Form.Control
                  type="text"
                  value={payableTo}
                  onChange={e => setPayableTo(e.target.value)}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Shipping Address</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={shippingAddress}
                  onChange={e => setShippingAddress(e.target.value)}
                />
              </Form.Group>
              <Form.Group>
                <Button
                  variant="outline-primary"
                  onClick={handleSendOtp}
                  disabled={sendingOtp || otpSent}
                  className="mt-2"
                >
                  {sendingOtp ? 'Sending OTP...' : otpSent ? 'OTP Sent' : 'Send OTP'}
                </Button>
              </Form.Group>
              {otpSent && (
                <Form.Group>
                  <Form.Label>Enter OTP</Form.Label>
                  <Form.Control
                    type="text"
                    value={otp}
                    onChange={e => setOtp(e.target.value)}
                    disabled={otpVerified}
                  />
                  <Button
                    variant="outline-success"
                    onClick={handleVerifyOtp}
                    disabled={verifyingOtp || otpVerified}
                    className="mt-2"
                  >
                    {verifyingOtp ? 'Verifying...' : otpVerified ? 'Verified' : 'Verify OTP'}
                  </Button>
                </Form.Group>
              )}
              {chequeError && <Alert variant="danger">{chequeError}</Alert>}
              {chequeSuccess && <Alert variant="success">{chequeSuccess}</Alert>}
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowChequeModal(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmitCheque}
              disabled={!otpVerified || submittingCheque || maxChequeAmount < 1}
            >
              {submittingCheque ? 'Submitting...' : 'Submit Request'}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
};

export default ACHTransferPage;
